import {
  Badge,
  Card,
  EmptyState,
  FreshnessBadge,
  Grid,
  Metric,
  PageHeader,
  PartialState,
  Skeleton,
  Stack,
  badgeToneForStatus,
} from "@evolua/ui";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QueryErrorState } from "../api/QueryErrorState.js";
import { useManagerDashboard } from "../api/use-manager.js";
import { useMeDashboard } from "../api/use-me.js";
import {
  ensureDemoOfflineSnapshot,
  writeOfflineSnapshotFromMeDashboard,
  type OfflineSnapshot,
} from "../pwa/offline-cache.js";
import { useOnlineStatus } from "../pwa/use-online-status.js";
import { hasPermission } from "../session/demo-session.js";
import { useSession } from "../session/SessionContext.js";
import { track } from "../telemetry/product-events.js";

function formatCurrency(amount: number): string {
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatCompetence(competence: string): string {
  const [year, month] = competence.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

function DashboardSkeleton() {
  return (
    <div className="evolua-zone" aria-busy="true">
      <Skeleton height="2rem" width="12rem" />
      <Grid min="14rem">
        <Skeleton height="7rem" />
        <Skeleton height="7rem" />
        <Skeleton height="7rem" />
      </Grid>
    </div>
  );
}

function EmployeeDashboard() {
  const query = useMeDashboard();

  useEffect(() => {
    if (!query.data) return;
    writeOfflineSnapshotFromMeDashboard({
      lastPayslip: query.data.lastPayslip
        ? {
            competence: query.data.lastPayslip.competence,
            netAmount: query.data.lastPayslip.netAmount,
          }
        : null,
      vacationBalance: query.data.vacationBalance
        ? { balanceDays: query.data.vacationBalance.balanceDays }
        : null,
    });
  }, [query.data]);

  if (query.isPending) return <DashboardSkeleton />;
  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const data = query.data;

  return (
    <Stack gap={6}>
      <section className="evolua-zone" aria-labelledby="zone-now">
        <h2 id="zone-now" className="evolua-zone__title">
          Precisa de você agora
        </h2>
        {data.pendingItems.length > 0 ? (
          <Card title="Pendências" variant="outlined">
            <ul className="evolua-list">
              {data.pendingItems.map((item) => (
                <li key={item.id} className="evolua-list__item">
                  <span>{item.label}</span>
                  <span className="evolua-muted">
                    <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
                    {item.dueDate ? ` · até ${item.dueDate}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState title="Nada pendente" description="Você está em dia no momento." />
        )}
      </section>

      <section className="evolua-zone" aria-labelledby="zone-numbers">
        <div className="evolua-spread">
          <h2 id="zone-numbers" className="evolua-zone__title">
            Seus números
          </h2>
          <FreshnessBadge syncedAt={data.syncedAt ?? null} />
        </div>
        <Grid min="14rem">
          <Metric
            label="Último holerite"
            value={data.lastPayslip ? formatCurrency(data.lastPayslip.netAmount) : "—"}
            hint={
              data.lastPayslip
                ? formatCompetence(data.lastPayslip.competence)
                : "Nenhum holerite disponível ainda."
            }
            provenance={
              <Link className="evolua-link" to={data.links.payslips}>
                Ver holerites
              </Link>
            }
          />
          <Metric
            label="Saldo de férias"
            value={
              data.vacationBalance ? `${data.vacationBalance.balanceDays} dias` : "—"
            }
            hint={
              data.vacationBalance
                ? `Limite para gozo: ${data.vacationBalance.limitDate}`
                : "Nenhum saldo de férias disponível."
            }
            provenance={
              <Link className="evolua-link" to={data.links.vacations}>
                Ver férias
              </Link>
            }
          />
        </Grid>
      </section>

      <section className="evolua-zone" aria-labelledby="zone-ctx">
        <h2 id="zone-ctx" className="evolua-zone__title">
          Contexto
        </h2>
        {data.announcements.length > 0 ? (
          <Card title="Comunicados">
            <ul className="evolua-list">
              {data.announcements.map((a) => (
                <li key={a.id}>
                  <Link className="evolua-link" to={`/comunicados/${a.id}`}>
                    {a.title}
                  </Link>
                  <p className="evolua-muted">{a.body}</p>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState
            title="Sem comunicados"
            description="Nenhum comunicado publicado no momento."
          />
        )}
      </section>
    </Stack>
  );
}

function ManagerDashboardView() {
  const query = useManagerDashboard();

  if (query.isPending) return <DashboardSkeleton />;
  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const data = query.data;

  return (
    <Stack gap={6}>
      <section className="evolua-zone" aria-labelledby="zone-now">
        <div className="evolua-spread">
          <h2 id="zone-now" className="evolua-zone__title">
            Precisa de você agora
          </h2>
          <FreshnessBadge syncedAt={data.syncedAt ?? null} />
        </div>
        <Grid min="14rem">
          <Metric
            label="Pendências de aprovação"
            value={data.pendingCount}
            provenance={
              <Link className="evolua-link" to={data.links.approvals}>
                Ir para aprovações
              </Link>
            }
          />
        </Grid>
        {data.pendingApprovals.length > 0 ? (
          <Card title="O que exige ação">
            <ul className="evolua-list">
              {data.pendingApprovals.map((item) => (
                <li key={item.id} className="evolua-list__item">
                  <span>
                    {item.label}{" "}
                    <span className="evolua-muted">
                      · {item.requesterName} ·{" "}
                      <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
                    </span>
                  </span>
                  <Link className="evolua-link" to={item.href}>
                    Abrir
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </section>

      <section className="evolua-zone" aria-labelledby="zone-numbers">
        <h2 id="zone-numbers" className="evolua-zone__title">
          Minha equipe · {data.teamSize} pessoas
        </h2>
        <Grid min="14rem">
          <Card title="Ausências no período">
            {data.absences.length > 0 ? (
              <ul className="evolua-list">
                {data.absences.map((item) => (
                  <li key={item.employeeId}>
                    <Link className="evolua-link" to={`/equipe/${item.employeeId}`}>
                      {item.fullName}
                    </Link>
                    <span className="evolua-muted">
                      {" "}
                      ·{" "}
                      <Badge tone={badgeToneForStatus(item.status)}>
                        {item.status === "ON_LEAVE" ? "Afastado" : "Férias"}
                      </Badge>
                      {item.endDate ? ` até ${item.endDate}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              "Nenhuma ausência registrada."
            )}
          </Card>
          <Card title="Aniversariantes">
            {data.birthdays.length > 0 ? (
              <ul className="evolua-list">
                {data.birthdays.map((item) => (
                  <li key={item.employeeId}>
                    <Link className="evolua-link" to={`/equipe/${item.employeeId}`}>
                      {item.fullName}
                    </Link>
                    <span className="evolua-muted">
                      {" "}
                      · {item.birthMonthDay}
                      {item.daysUntil === 0
                        ? " · hoje"
                        : ` · em ${item.daysUntil} dia(s)`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              "Nenhum aniversário próximo."
            )}
          </Card>
        </Grid>
      </section>

      <section className="evolua-zone" aria-labelledby="zone-ctx">
        <h2 id="zone-ctx" className="evolua-zone__title">
          Contexto
        </h2>
        {data.alerts.length > 0 ? (
          <Card title="Alertas do time">
            <ul className="evolua-list">
              {data.alerts.map((alert) => (
                <li key={alert.id}>
                  <Badge tone={badgeToneForStatus(alert.severity)}>
                    {alert.severity}
                  </Badge>{" "}
                  {alert.message}{" "}
                  {alert.href ? (
                    <Link className="evolua-link" to={alert.href}>
                      Ver
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState
            title="Sem alertas"
            description="Nenhum alerta na sua equipe no momento."
          />
        )}
        <p>
          <Link className="evolua-link" to={data.links.team}>
            Ver equipe completa
          </Link>
          {" · "}
          <Link className="evolua-link" to={data.links.vacationCalendar}>
            Calendário de férias
          </Link>
          {" · "}
          <Link className="evolua-link" to={data.links.teamIndicators}>
            Indicadores
          </Link>
        </p>
      </section>
    </Stack>
  );
}

export function HomePage() {
  const { session, isDemoMode } = useSession();
  const online = useOnlineStatus();
  const [snapshot, setSnapshot] = useState<OfflineSnapshot | null>(null);

  useEffect(() => {
    setSnapshot(ensureDemoOfflineSnapshot());
  }, []);

  useEffect(() => {
    if (!session) return;
    track("dashboard.viewed");
  }, [session?.userId, session?.companyId]);

  if (!session) return null;

  const company = session.companies.find((c) => c.id === session.companyId)?.name ?? "—";
  // Gestor: Home mostra dashboard de equipe; self-service (/holerite, /ferias…)
  // usa ME_SUBJECT_EMPLOYEE_MAP (demo-manager → Bruno).
  const canSeeManagerDashboard =
    session.role === "MANAGER" ||
    (hasPermission(session, ["vacation:approve"]) &&
      !hasPermission(session, ["vacation:approve_on_behalf"]));
  const canSeeMeDashboard =
    !canSeeManagerDashboard && hasPermission(session, ["lgpd:export_own"]);

  return (
    <>
      <PageHeader
        title={`Olá, ${session.displayName}`}
        lead={`Portal único (D-018). Perfil: ${session.role} · ${company}.${
          isDemoMode
            ? " Modo demo — troque o perfil no menu da conta."
            : " Sessão autenticada via API."
        }`}
        {...(!canSeeMeDashboard && !canSeeManagerDashboard && snapshot
          ? { freshness: <FreshnessBadge syncedAt={snapshot.updatedAt} /> }
          : {})}
      />

      {!online ? (
        <PartialState description="Offline — holerite e saldo abaixo vêm do cache local deste dispositivo." />
      ) : null}

      {canSeeManagerDashboard ? (
        <ManagerDashboardView />
      ) : canSeeMeDashboard ? (
        <EmployeeDashboard />
      ) : (
        <Grid min="14rem">
          <Card title="Último holerite">
            {snapshot ? (
              <>
                Competência {snapshot.holerite.competence}
                <br />
                Líquido {snapshot.holerite.netPayLabel}
                {!online ? <p className="evolua-muted">(cache)</p> : null}
              </>
            ) : (
              "Sem dados em cache."
            )}
          </Card>
          <Card title="Saldo de férias">
            {snapshot ? (
              <>
                {snapshot.vacation.balanceDays} dias
                {!online ? <p className="evolua-muted">(cache)</p> : null}
              </>
            ) : (
              "Sem dados em cache."
            )}
          </Card>
        </Grid>
      )}
    </>
  );
}
